// #417 (DecisionLog §3.13 #4). The words for an organisation member seated on
// NO location (membershipStatus 7, NoLocationMembership): approved, paid up,
// and shown a near-empty store because every ship-to read fails closed for
// them. Organisation membership does not grant location membership — locations
// are assigned deliberately, because the ship-to address is a compliance
// boundary (§3.12) — so the way out is a person, not a button, and every
// surface says so in the same breath. That person is USNara support: the
// platform launches with one user per organization and no team management
// (OB-07, #453), so there is no organization admin to ask.
//
// One file so the PO reads every sentence in one place. Informational copy:
// nothing here may read as an error, a lapse or a wall — the gate lets this
// member through by contract and they may still browse unrestricted products.

// The words SupportLinkedText turns into a link to the support page wherever
// these sentences render as a node.
export const SUPPORT_LINK_TEXT = "USNara support";

const NO_LOCATION_CONTACT_SUPPORT =
    `Contact ${SUPPORT_LINK_TEXT} to be added to a shipping location.`;

// The account-level note (dashboard, membership page, the header's switcher),
// where the catalogue really is filtered.
export const NO_LOCATION_NOTE =
    "You're not assigned to a shipping location yet, so some products can't " +
    `be shown for your account. ${NO_LOCATION_CONTACT_SUPPORT}`;

// The cart and checkout note while the cart holds restricted lines. Here the
// restricted lines are listed right below the note, so "can't be shown" would
// be false; what is true is that they can't be ordered.
export const NO_LOCATION_CART_NOTE =
    "Some items in your cart can't be ordered because you're not assigned to " +
    `a shipping location yet. ${NO_LOCATION_CONTACT_SUPPORT}`;

// Checkout's Place Order disabled reason, and the cart and checkout note when
// no line is restricted. This member can place no order at all — there is no
// eligible ship-to location — so removing lines never unblocks them; only
// USNara support can.
export const NO_LOCATION_ORDER_BLOCKED =
    "You can't place orders until you're assigned to a shipping location. " +
    NO_LOCATION_CONTACT_SUPPORT;

// The per-line flag on a restricted cart line for this member. "Can't ship to
// your selected location" is the wrong reason for someone with no location
// selected, and would contradict the note above the list.
export const NO_LOCATION_LINE_MESSAGE = "Can't be ordered for your account";

// The subtotal caveat beside every figure that leaves restricted lines out:
// the cart summary, the cart's own bottom-of-list subtotal and the checkout
// summary — one sentence, built here, so the three cannot drift apart.
//
// A member with a location is promised that removing the lines lets the order
// through, which is true for them. A member on no location is not: they can
// place no order at all, so they get the first sentence only.
export const excludedCaveat = (
    count: number,
    hasNoLocationMembership: boolean,
): string => {
    const lines = `${count} ${count === 1 ? "item" : "items"}`;

    if (hasNoLocationMembership) {
        return `Excludes ${lines} that can't be ordered for your account.`;
    }

    return (
        `Excludes ${lines} that can't ship to your selected location. ` +
        `This order can't be placed until ${count === 1 ? "it's" : "they're"} removed.`
    );
};

// The product not-available view, keyed on restriction.reason === NoDestination.
// The product is on screen, so "can't be shown" would be false; what is true
// is that it can't be ordered.
export const NO_LOCATION_PRODUCT_MESSAGE =
    "This product can't be ordered for your account until you're assigned " +
    `to a shipping location. ${NO_LOCATION_CONTACT_SUPPORT}`;
